import type { ApiResponse, CursorPage } from '../../api/contract';
import type { ApiClient, RequestOptions } from '../../api/http';
import { normalizeReportFilters } from '../reports/realRepository';
import type { ReportKind } from '../reports/types';
import type { ReportExportsRepository } from './repository';
import type { ReportExport, ReportExportApiType, ReportExportListQuery } from './types';

type ExportListResponse = ApiResponse<'/api/v1/report-exports', 'get'>;
type ExportResponse = ApiResponse<'/api/v1/report-exports/{id}', 'get'>;
type CreateExportResponse = ApiResponse<'/api/v1/report-exports', 'post'>;

export const reportExportType: Readonly<Record<ReportKind, ReportExportApiType>> = Object.freeze({
  transactions: 'TRANSACTIONS',
  'agent-commissions': 'AGENT_COMMISSIONS',
  'affiliate-commissions': 'AFFILIATE_COMMISSIONS',
  games: 'GAMES',
  registrations: 'REGISTRATIONS',
  'partner-reconciliation': 'PARTNER_RECONCILIATION',
  'commission-rule-usage': 'COMMISSION_RULE_USAGE',
  'audit-activity': 'AUDIT_ACTIVITY',
  wallets: 'WALLETS',
  'wallet-ledger': 'WALLET_LEDGER',
  withdrawals: 'WITHDRAWALS',
  'partner-credit-reconciliation': 'PARTNER_CREDIT_RECONCILIATION',
  'user-affiliate-by-game': 'USER_AFFILIATE_BY_GAME',
});

export function createRealReportExportsRepository(
  client: ApiClient,
  accessToken: string | null,
): ReportExportsRepository {
  const auth: RequestOptions = accessToken === null ? {} : { accessToken };
  return {
    async list(query, signal) {
      const response = await client.get<ExportListResponse>('/api/v1/report-exports', {
        ...auth,
        query: listQuery(query),
        signal,
      });
      return page(response);
    },
    async create(input, idempotencyKey) {
      const type = apiReportType(input.reportType);
      const response = await client.post<CreateExportResponse>(
        '/api/v1/report-exports',
        {
          reportType: type,
          format: 'CSV',
          filters: normalizeReportFilters(input.reportType, input.filters),
        },
        { ...auth, idempotencyKey: requireUuidV4(idempotencyKey) },
      );
      return { data: response.data.data, requestId: response.requestId };
    },
    async get(exportId, signal) {
      const response = await client.get<ExportResponse>(exportPath(exportId), { ...auth, signal });
      return { ...response.data.data, requestId: response.requestId };
    },
    download(exportId, signal) {
      return client.download(`${exportPath(exportId)}/download`, { ...auth, signal });
    },
  };
}

function listQuery(query: ReportExportListQuery): Readonly<Record<string, unknown>> {
  return compact({
    reportType: query.filters.reportType === '' ? '' : apiReportType(query.filters.reportType),
    status: query.filters.status,
    requestedBy: query.filters.requestedBy.trim(),
    partnerId: query.filters.partnerId.trim(),
    agentId: query.filters.agentId.trim(),
    from: query.filters.from.trim(),
    to: query.filters.to.trim(),
    cursor: query.cursor,
    limit: query.pageSize,
  });
}

function page(response: {
  readonly data: ExportListResponse;
  readonly requestId: string | null;
}): CursorPage<ReportExport> {
  return {
    items: response.data.data,
    nextCursor: response.data.pagination.nextCursor,
    requestId: response.requestId,
  };
}

function apiReportType(kind: ReportKind): ReportExportApiType {
  const type = reportExportType[kind];
  if (type === undefined) throw new Error('Unsupported report kind');
  return type;
}

function exportPath(exportId: string): string {
  return `/api/v1/report-exports/${encodeURIComponent(exportId)}`;
}

function compact(values: Readonly<Record<string, unknown>>): Readonly<Record<string, unknown>> {
  return Object.fromEntries(Object.entries(values).filter(([, value]) => value !== ''));
}

function requireUuidV4(value: string): string {
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(value))
    throw new Error('A UUID v4 idempotency key is required');
  return value;
}
